import { AuthService } from '../services/auth.service';

export interface SystemMetadata {
  createdBy?: string;
  updatedBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date | null;
  deletedBy?: string;
}

/**
 * Build system metadata for create/update operations
 */
export function buildSystemMetadata(authService: AuthService, isCreate: boolean): { __system: SystemMetadata } {
  const currentUser = authService.currentUser();
  const currentUserProfile = authService.currentUserProfile();
  const currentUserId = currentUser?.uid || currentUserProfile?.uid || currentUserProfile?.id;
  const now = new Date();

  const systemMetadata: SystemMetadata = {
    updatedBy: currentUserId || '',
    updatedAt: now
  };

  if (isCreate) {
    systemMetadata.createdBy = currentUserId || '';
    systemMetadata.createdAt = now;
  }

  return { __system: systemMetadata };
}

/**
 * Coerce API/Firestore timestamp shapes to Date.
 * Supports: epoch ms number, ISO string, Date, Firestore Timestamp.toDate(),
 * and serialized Firestore { _seconds, _nanoseconds }.
 */
export function coerceTimestampToDate(value: unknown): Date | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value === 'number' && !isNaN(value)) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Postgres BIGINT often arrives as a numeric string (invalid for Date.parse).
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const asNumber = Number(trimmed);
      if (!isNaN(asNumber)) {
        const d = new Date(asNumber);
        return isNaN(d.getTime()) ? undefined : d;
      }
    }
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? undefined : d;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (typeof o['toDate'] === 'function') {
      try {
        const d = (o['toDate'] as () => Date)();
        return d instanceof Date && !isNaN(d.getTime()) ? d : undefined;
      } catch {
        return undefined;
      }
    }
    // Firestore client ({ seconds, nanoseconds }) and legacy ({ _seconds, _nanoseconds })
    const secRaw = o['_seconds'] ?? o['seconds'];
    const nsRaw = o['_nanoseconds'] ?? o['nanoseconds'];
    if (typeof secRaw === 'number' && !isNaN(secRaw)) {
      const ns = typeof nsRaw === 'number' && !isNaN(nsRaw) ? nsRaw : 0;
      const d = new Date(secRaw * 1000 + ns / 1e6);
      return isNaN(d.getTime()) ? undefined : d;
    }
  }
  return undefined;
}

/**
 * Safely extract createdAt from item (top-level or __system).
 * Handles Date, epoch ms, ISO strings, Firestore Timestamp, and { _seconds, _nanoseconds }.
 */
export function getCreatedAt(item: any): Date | undefined {
  if (!item) return undefined;

  const fromTop = coerceTimestampToDate(item.createdAt);
  if (fromTop) return fromTop;

  const createdAt = item.__system?.createdAt;
  return coerceTimestampToDate(createdAt);
}

/**
 * Safely extract updatedAt from item (top-level or __system).
 */
export function getUpdatedAt(item: any): Date | undefined {
  if (!item) return undefined;

  const fromTop = coerceTimestampToDate(item.updatedAt);
  if (fromTop) return fromTop;

  const updatedAt = item.__system?.updatedAt;
  return coerceTimestampToDate(updatedAt);
}

